import { create } from 'zustand';

// Presse-papiers des menus contextuels : Copier / Couper / Coller, pour les MAILS
// et pour les DOSSIERS.
//
// UN SEUL PRESSE-PAPIERS À LA FOIS. Copier un dossier vide le presse-papiers de
// mails, et réciproquement : « Coller » doit avoir un sens unique, comme dans un
// explorateur de fichiers. Les deux états vivent dans le MÊME module pour que
// cette exclusion soit écrite une seule fois, sans import croisé entre stores.
//
// Un presse-papiers 'copy' survit à plusieurs collages ; un 'cut' est vidé après
// le premier collage réussi (sémantique de déplacement).

export type ClipboardMode = 'copy' | 'cut';

export interface ClipboardMail {
  mailId: string;
  /** Dossier courant (`null` = racine du projet). Sert à ignorer un collage sur place. */
  folderId: string | null;
  containerId: string;
  label: string;
}

export interface ClipboardFolder {
  folderId: string;
  containerId: string;
  /**
   * Parent courant : `null` = racine du projet, ABSENT = inconnu.
   *
   * Ne sert qu'à ignorer un « couper-coller » sur place. La distinction compte :
   * un résultat de recherche ne porte pas le parent de son dossier (`fn_search`
   * renvoie le nom du parent, pas son id), et écrire `null` dans ce cas
   * affirmerait « ce dossier est à la racine » — un mensonge, alors que le champ
   * est seulement inconnu. Facultatif, le raccourci de non-opération est
   * simplement sauté : le serveur reconnaît un déplacement sans effet.
   */
  parentId?: string | null;
  name: string;
}

interface MailClipboardState {
  mode: ClipboardMode | null;
  items: ClipboardMail[];
  copyMails: (items: ClipboardMail[]) => void;
  cutMails: (items: ClipboardMail[]) => void;
  clear: () => void;
}

interface FolderClipboardState {
  mode: ClipboardMode | null;
  folder: ClipboardFolder | null;
  copyFolder: (folder: ClipboardFolder) => void;
  cutFolder: (folder: ClipboardFolder) => void;
  clear: () => void;
}

export const useMailClipboardStore = create<MailClipboardState>((set) => ({
  mode: null,
  items: [],
  copyMails: (items) => {
    clearFolderClipboard();
    set({ mode: 'copy', items });
  },
  cutMails: (items) => {
    clearFolderClipboard();
    set({ mode: 'cut', items });
  },
  clear: () => set({ mode: null, items: [] }),
}));

export const useFolderClipboardStore = create<FolderClipboardState>((set) => ({
  mode: null,
  folder: null,
  copyFolder: (folder) => {
    clearMailClipboard();
    set({ mode: 'copy', folder });
  },
  cutFolder: (folder) => {
    clearMailClipboard();
    set({ mode: 'cut', folder });
  },
  clear: () => set({ mode: null, folder: null }),
}));

// Déclarées après les deux stores : appelées à l'exécution, jamais au chargement
// du module.
function clearFolderClipboard() {
  useFolderClipboardStore.setState({ mode: null, folder: null });
}
function clearMailClipboard() {
  useMailClipboardStore.setState({ mode: null, items: [] });
}
